import React, { useState, useContext } from 'react';
import { Search, Plus, MapPin } from 'lucide-react';
import { Container, Row, Col, Form, InputGroup, Button } from 'react-bootstrap';
import axios from 'axios';

import AppContext from '../../../context/AppContext';
import AddEditDestinationForm   from '../../../components/admin/AdminDestinationManagement/AddEditDestinationForm';
import DestinationDetailsModal  from '../../../components/admin/AdminDestinationManagement/DestinationDetailsModal';
import DestinationCard          from '../../../components/admin/AdminDestinationManagement/DestinationCard';

const DestinationList = () => { 
  const {
    baseUrl, authToken,
    destinations, setDestinations,
    showDestinationToast,
    setLoading, loading,
  } = useContext(AppContext);

  const [searchTerm,           setSearchTerm]           = useState('');
  const [showForm,             setShowForm]             = useState(false);
  const [showDetails,          setShowDetails]          = useState(false);
  const [selectedDestination,  setSelectedDestination]  = useState(null);

  const openAdd = () => {
    setSelectedDestination(null);
    setShowForm(true);
  };

  const openEdit = (dest) => {
    setSelectedDestination(dest);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedDestination(null);
  };

  const openDetails = (dest) => {
    setSelectedDestination(dest);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedDestination(null);
  };

  const handleDelete = (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setLoading(true);
    axios.delete(`${baseUrl}/admin/delete-destination/${id}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          setDestinations((prev) => prev.filter((d) => d.id !== id));
          showDestinationToast('Destination deleted successfully!', 'success');
        } else {
          showDestinationToast(res.data.message || 'Something went wrong', 'error');
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error('Delete error:', err);
        showDestinationToast('Unable to connect to server', 'error');
      });
  };

  const handleStatusToggle = (id, currentStatus) => {
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    setLoading(true);
    axios.post(
      `${baseUrl}/admin/update-destination/${id}`,
      { status: newStatus },
      { headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' } }
    )
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          setDestinations((prev) =>
            prev.map((d) => d.id === id ? { ...d, status: newStatus } : d)
          );
          showDestinationToast(`Destination set to ${newStatus}`, 'success');
        } else {
          showDestinationToast(res.data.message || 'Something went wrong', 'error');
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error('Status toggle error:', err);
        showDestinationToast('Unable to connect to server', 'error');
      });
  };

  const filtered = destinations.filter((d) => {
    const term = searchTerm.toLowerCase();
    const name = (d.destination_name || d.name || '').toLowerCase();
    return (
      name.includes(term) ||
      (d.province || '').toLowerCase().includes(term) ||
      (d.district || '').toLowerCase().includes(term)
    );
  });

  return (
    <>
      <Container fluid className="adm-dest-main-container">

        <Row className="adm-dest-header-gradient mb-4">
          <Col className="py-4">
            <h1 className="adm-dest-main-title text-white mb-1">Destinations Management</h1>
            <p className="adm-dest-main-subtitle text-white mb-0">Manage all travel destinations</p>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="adm-dest-search-input-group">
                <InputGroup>
                  <InputGroup.Text className="adm-dest-search-icon"><Search size={18} /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by name, province or district..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="adm-dest-search-control"
                  />
                </InputGroup>
              </div>
              <Button className="adm-dest-add-main-btn" onClick={openAdd}>
                <Plus size={20} /> Add New Destination
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <span className="adm-dest-stats-text">
              Showing {filtered.length} {filtered.length === 1 ? 'destination' : 'destinations'}
            </span>
          </Col>
        </Row>

        {/* Cards */}
        {loading ? (
          <Row>
            <Col className="text-center py-5">
              <div className="adm-dest-loading">Loading destinations...</div>
            </Col>
          </Row>
        ) : filtered.length === 0 ? (
          <Row>
            <Col className="text-center py-5">
              <div className="adm-dest-empty-state">
                <MapPin size={48} className="adm-dest-empty-icon mb-3" />
                <p className="adm-dest-empty-text">
                  {searchTerm ? 'No destinations match your search.' : 'No destinations found.'}
                </p>
                {!searchTerm && (
                  <Button className="adm-dest-add-main-btn mt-3" onClick={openAdd}>
                    <Plus size={20} /> Add Your First Destination
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        ) : (
          <Row className="adm-dest-cards-grid">
            {filtered.map((dest) => (
              <Col lg={3} md={4} sm={6} xs={12} key={dest.id} className="mb-4">
                <DestinationCard
                  destination={dest}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onViewDetails={openDetails}
                  onStatusToggle={handleStatusToggle}
                />
              </Col>
            ))}
          </Row>
        )}
      </Container>
      <AddEditDestinationForm
        show={showForm}
        onHide={closeForm}
        destination={selectedDestination}
      />

      <DestinationDetailsModal
        show={showDetails}
        onHide={closeDetails}
        destination={selectedDestination}
      />
    </>
  );
};

export default DestinationList;